import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  message,
  Row,
  Col,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import useAuthStore from "../../store/authStore";
import useDealStore from "../../store/dealStore";
import useSettingsStore from "../../store/settingsStore";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const DealForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const {
    currentDeal,
    createDeal,
    updateDeal,
    fetchDealById,
    clearCurrentDeal,
    stageProbabilities,
    fetchStageProbabilities,
  } = useDealStore();
  const { currencySymbol } = useSettingsStore();

  const [form] = Form.useForm();
  const stageValue = Form.useWatch("stage", form);
  const stageSummaryStages = [
    { key: "qualification", label: "Qualification" },
    { key: "needs_analysis", label: "Needs Analysis" },
    { key: "proposal", label: "Proposal" },
    { key: "negotiation", label: "Negotiation" },
    { key: "closed_won", label: "Closed Won" },
  ];
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEditing) {
      fetchDealById(id);
    } else {
      clearCurrentDeal();
      form.resetFields();
    }
  }, [isEditing, id, fetchDealById, clearCurrentDeal, form]);

  useEffect(() => {
    fetchStageProbabilities();
  }, [fetchStageProbabilities]);

  useEffect(() => {
    // This separate hook is dedicated solely to populating the form
    // fields when a deal is fetched for editing.
    if (isEditing && currentDeal) {
      form.setFieldsValue({
        title: currentDeal.title,
        company: currentDeal.contact.company,
        contact: {
          firstName: currentDeal.contact?.firstName || "",
          lastName: currentDeal.contact?.lastName || "",
          email: currentDeal.contact?.email || "",
          phone: currentDeal.contact?.phone || "",
          company: currentDeal.contact?.company || "",
          jobTitle: currentDeal.contact?.jobTitle || "",
        },
        value: currentDeal.value,
        stage: currentDeal.stage,
        probability: currentDeal.probability,
        expectedCloseDate: currentDeal.expectedCloseDate
          ? dayjs(currentDeal.expectedCloseDate)
          : null,
        source: currentDeal.source,
        description: currentDeal.description,
        notes: currentDeal.notes,
      });
    } else if (!isEditing) {
      // For the create page, ensure the form is cleared if it wasn't on mount.
      form.resetFields();
    }
  }, [isEditing, currentDeal, form]);

  useEffect(() => {
    if (!stageValue) {
      return;
    }

    const probabilityFromStage = stageProbabilities?.[stageValue];
    if (typeof probabilityFromStage === "number") {
      form.setFieldsValue({ probability: probabilityFromStage });
    }
  }, [stageValue, stageProbabilities, form]);

  const handleSubmit = async (values) => {
    setSubmitting(true);

    const { contact, value, expectedCloseDate, ...rest } = values;

    // Clean up contact object - only include non-empty fields
    const cleanContact = {};
    if (contact?.firstName?.trim())
      cleanContact.firstName = contact.firstName.trim();
    if (contact?.lastName?.trim())
      cleanContact.lastName = contact.lastName.trim();
    if (contact?.email?.trim()) cleanContact.email = contact.email.trim();
    if (contact?.phone?.trim()) cleanContact.phone = contact.phone.trim();
    if (contact?.company?.trim()) cleanContact.company = contact.company.trim();
    if (contact?.jobTitle?.trim())
      cleanContact.jobTitle = contact.jobTitle.trim();

    const dealData = {
      ...rest,
      ...(Object.keys(cleanContact).length > 0 && { contact: cleanContact }),
      value: value,
      ...(expectedCloseDate && {
        expectedCloseDate: expectedCloseDate.toISOString(),
      }),
    };

    // Clean up any empty string fields that should be omitted
    Object.keys(dealData).forEach((key) => {
      if (
        dealData[key] === "" ||
        dealData[key] === null ||
        dealData[key] === undefined
      ) {
        delete dealData[key];
      }
    });

    try {
      let result;
      if (isEditing) {
        result = await updateDeal(id, dealData);
      } else {
        result = await createDeal(dealData);
      }

      if (result.success) {
        message.success(
          `Deal ${isEditing ? "updated" : "created"} successfully`
        );
        navigate("/deals");
      } else {
        message.error(
          result.error || `Failed to ${isEditing ? "update" : "create"} deal`
        );
      }
    } catch (error) {
      message.error(`Failed to ${isEditing ? "update" : "create"} deal`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/deals");
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleCancel}
          className="mb-4"
        >
          Back to Deals
        </Button>
        <Title level={2} className="mb-2">
          {isEditing ? "Edit Deal" : "Create New Deal"}
        </Title>
      </div>

      <Row gutter={24}>
        <Col span={16}>
          <Card className="shadow-soft">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                stage: "prospecting",
                probability: stageProbabilities?.prospecting ?? 10,
                source: "other", // Changed from "website" to "other"
              }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="title"
                    label="Deal Title"
                    rules={[
                      { required: true, message: "Please enter deal title" },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (/^\s|\s$/.test(value)) {
                            return Promise.reject(
                              new Error("No leading or trailing spaces allowed")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Enter deal title" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="company"
                    label="Company"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || typeof value !== "string") {
                            return Promise.resolve();
                          }
                          const trimmedValue = value.trim();
                          if (!trimmedValue) {
                            return Promise.resolve();
                          }
                          if (/^\d+$/.test(trimmedValue)) {
                            return Promise.reject(
                              new Error("Company name cannot be numeric")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Enter company name" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">Contact Information</Divider>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={["contact", "firstName"]}
                    label="Contact First Name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter contact first name",
                      },
                      {
                        validator: (_, value) => {
                          if (!value || typeof value !== "string") {
                            return Promise.resolve();
                          }
                          const trimmedValue = value.trim();
                          if (!trimmedValue) {
                            return Promise.resolve();
                          }
                          if (!/^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/.test(trimmedValue)) {
                            return Promise.reject(
                              new Error("Please enter a valid contact name")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (/^\s|\s$/.test(value)) {
                            return Promise.reject(
                              new Error("No leading or trailing spaces allowed")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Enter first name" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["contact", "lastName"]}
                    label="Contact Last Name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter contact last name",
                      },
                      {
                        validator: (_, value) => {
                          if (!value || typeof value !== "string") {
                            return Promise.resolve();
                          }
                          const trimmedValue = value.trim();
                          if (!trimmedValue) {
                            return Promise.resolve();
                          }
                          if (!/^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/.test(trimmedValue)) {
                            return Promise.reject(
                              new Error("Please enter a valid contact name")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          if (/^\s|\s$/.test(value)) {
                            return Promise.reject(
                              new Error("No leading or trailing spaces allowed")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Enter last name" />
                  </Form.Item>
                </Col>                <Col span={8}>
                  <Form.Item
                    name={["contact", "email"]}
                    label="Contact Email"
                    rules={[
                      { required: true, message: "Please enter contact email" },
                      { type: "email", message: "Please enter valid email" },
                    ]}
                  >
                    <Input placeholder="Enter email address" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name={["contact", "phone"]} label="Contact Phone">
                    <Input placeholder="Enter phone number" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["contact", "company"]}
                    label="Contact Company"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || typeof value !== "string") {
                            return Promise.resolve();
                          }
                          const trimmedValue = value.trim();
                          if (!trimmedValue) {
                            return Promise.resolve();
                          }
                          if (/^\d+$/.test(trimmedValue)) {
                            return Promise.reject(
                              new Error("Company name cannot be numeric")
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Enter contact's company" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["contact", "jobTitle"]}
                    label="Contact Job Title"
                  >
                    <Input placeholder="Enter contact's job title" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">Deal Details</Divider>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="value"
                    label={`Deal Value (${currencySymbol || "$"})`}
                    rules={[
                      { required: true, message: "Please enter deal value" },
                    ]}
                  >
                    <InputNumber
                      placeholder="0"
                      min={0}
                      formatter={(value) =>
                        `${currencySymbol || "$"} ${value}`.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ","
                        )
                      }
                      parser={(value) => value.replace(/[^\d]/g, "")}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="stage"
                    label="Deal Stage"
                    rules={[
                      { required: true, message: "Please select deal stage" },
                    ]}
                  >
                    <Select placeholder="Select stage">
                      <Option value="prospecting">Prospecting</Option>
                      <Option value="qualification">Qualification</Option>
                      <Option value="needs_analysis">Needs Analysis</Option>
                      <Option value="proposal">Proposal</Option>
                      <Option value="negotiation">Negotiation</Option>
                      <Option value="closed_won">Closed Won</Option>
                      <Option value="closed_lost">Closed Lost</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="probability"
                    label="Win Probability (%)"
                    rules={[
                      {
                        required: true,
                        message: "Please enter win probability",
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      readOnly
                      controls={false}
                      formatter={(value) => `${value}%`}
                      parser={(value) => value.replace("%", "")}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="expectedCloseDate"
                    label="Expected Close Date"
                    rules={[
                      {
                        required: true,
                        message: "Please select expected close date",
                      },
                    ]}
                  >
                    <DatePicker
                      placeholder="Select expected close date"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="source"
                    label="Lead Source"
                    rules={[
                      { required: true, message: "Please select lead source" },
                    ]}
                  >
                    <Select placeholder="Select source">
                      <Option value="inbound">Inbound</Option>
                      <Option value="outbound">Outbound</Option>
                      <Option value="referral">Referral</Option>
                      <Option value="partner">Partner</Option>
                      <Option value="marketing">Marketing</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">Additional Information</Divider>

              <Form.Item name="description" label="Deal Description">
                <TextArea
                  rows={4}
                  placeholder="Enter deal description, requirements, or key details..."
                />
              </Form.Item>

              <Form.Item name="notes" label="Notes">
                <TextArea
                  rows={3}
                  placeholder="Enter any additional notes..."
                />
              </Form.Item>

              <Form.Item className="mb-0">
                <Space>
                  <Button onClick={handleCancel}>Cancel</Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitting}
                    icon={<SaveOutlined />}
                  >
                    {isEditing ? "Update Deal" : "Create Deal"}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col span={8}>
          <Card className="shadow-soft">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <DollarOutlined className="text-2xl text-green-600" />
              </div>
              <Title level={4} className="mb-1">
                Deal Information
              </Title>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
              <Title level={5} className="text-blue-800 mb-2">
                Deal Stages
              </Title>
              <div className="space-y-2 text-sm">
                {stageSummaryStages.map(({ key, label }) => {
                  const probability = stageProbabilities?.[key];
                  if (typeof probability !== "number") {
                    return null;
                  }

                  const valueClass =
                    key === "closed_won" ? "text-green-600" : "text-blue-600";

                  return (
                    <div key={key} className="flex justify-between">
                      <span>{label}</span>
                      <span className={valueClass}>{probability}%</span>
                    </div>
                  );
                })}
              </div>
            </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <Title level={5} className="text-orange-800 mb-2">
                  Tips
                </Title>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Set realistic deal values and probabilities</li>
                  <li>• Update deal stages as progress is made</li>
                  <li>• Add detailed notes for better tracking</li>
                  <li>• Set expected close dates for pipeline management</li>
                </ul>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DealForm;
